import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUser } from "@/contexts/user";

export default function DataDeletion() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isAuthenticated = !!user?.id;

  return (
    <div className="min-h-screen bg-background p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(isAuthenticated ? "/profile" : "/auth")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isAuthenticated ? "Back to Profile" : "Back to Login"}
        </Button>
        <Card className="p-8">
          <h1 className="text-3xl font-bold mb-6">Data Deletion Policy</h1>
          <ScrollArea className="h-[calc(100vh-250px)]">
            <div className="space-y-6 text-muted-foreground pr-4">
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">Your Right to Delete</h2>
                <p>You have the right to delete your account and all associated data at any time. We are committed to making this process simple and transparent.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">How to Delete Your Account</h2>
                <ol className="list-decimal list-inside ml-4 mt-2 space-y-2">
                  <li>Log in to your StudyForge account</li>
                  <li>Navigate to Profile → Settings</li>
                  <li>Scroll to "Danger Zone"</li>
                  <li>Click "Delete Account"</li>
                  <li>Confirm by typing your username</li>
                  <li>Your account will be permanently deleted</li>
                </ol>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">What Data Gets Deleted</h2>
                <p>When you delete your account, the following data is permanently removed:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>User profile (username, email, avatar)</li>
                  <li>All study session records</li>
                  <li>Daily totals and statistics</li>
                  <li>Leaderboard entries</li>
                  <li>Follow/friend relationships</li>
                  <li>Achievement badges</li>
                  <li>Email preferences</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">Timeline for Deletion</h2>
                <p><strong>Immediate:</strong> Your account is deactivated and you are logged out immediately.</p>
                <p className="mt-2"><strong>Within 30 days:</strong> All personal data is permanently deleted from our systems.</p>
                <p className="mt-2"><strong>Backups:</strong> Data in encrypted backups will be deleted within 90 days as part of our regular backup rotation.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">What We Retain (Anonymized)</h2>
                <p>For legal and analytical purposes, we may retain anonymized aggregate statistics that cannot be linked back to you:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Total number of study sessions (count only)</li>
                  <li>General usage patterns (no personal identifiers)</li>
                  <li>Anonymous analytics data</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">Cannot Delete Your Account?</h2>
                <p>If you encounter any issues deleting your account, please:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Use the feedback feature in the app (select "Bug" category)</li>
                  <li>Email us at support@studyforge.app</li>
                  <li>We will manually process your deletion request within 72 hours</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">Data Export Before Deletion</h2>
                <p>If you want to keep a copy of your data before deleting:</p>
                <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
                  <li>Contact us at export@studyforge.app</li>
                  <li>We will provide a JSON file with all your data within 7 days</li>
                  <li>After receiving your export, proceed with account deletion</li>
                </ol>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-3">GDPR & CCPA Compliance</h2>
                <p>This policy complies with:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>GDPR Article 17:</strong> Right to erasure ("right to be forgotten")</li>
                  <li><strong>CCPA Section 1798.105:</strong> Right to deletion</li>
                  <li><strong>App Store Requirements:</strong> Clear data deletion process</li>
                </ul>
              </section>

              <p className="text-sm mt-8 pt-6 border-t">
                Last updated: January 2025
              </p>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
